'use client';

import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';
import { FilterState, FilterAction, FilterContextType, ApiFilterResponse } from '@interfaces/filter.types';
import { filterService } from '@services/filterService';
import { useIsMobile } from '@hooks/use-mobile';
import { useSearchParams } from 'next/navigation';
import { parseUrlFilters, updateUrlWithFilters, hasUrlFilters } from '@utils/urlFilterUtils';

const initialState: FilterState = {
  body_type: [],
  brand_model: [],
  fuel_type: [],
  transmission: [],
  budget: { min: 300000, max: 30000000 },
  is_express_delivery: false,
  feature: [],
  sort_by: [],
  seat_capacity: [],
  appliedFilters: {},
};

const filterReducer = (state: FilterState, action: FilterAction): FilterState => {
  switch (action.type) {
    case 'SET_FILTER':
      if (!action.category || !action.values) return state;
      return {
        ...state,
        [action.category]: action.values
      };

    case 'SET_BUDGET':
      return {
        ...state,
        budget: action.values as { min: number; max: number }
      };

    case 'SET_EXPRESS_DELIVERY':
      return {
        ...state,
        is_express_delivery: action.value as boolean
      };

    case 'APPLY_EXPRESS_DELIVERY_FILTER':
      return {
        ...state,
        is_express_delivery: action.value as boolean,
        appliedFilters: {
          ...state.appliedFilters,
          is_express_delivery: action.value as boolean
        }
      };

    case 'APPLY_FILTERS':
      const budgetDefaults = action.budgetDefaults || { min: 300000, max: 30000000 };
      return {
        ...state,
        appliedFilters: {
          body_type: state.body_type.length > 0 ? state.body_type : undefined,
          brand_model: state.brand_model.length > 0 ? state.brand_model : undefined,
          fuel_type: state.fuel_type.length > 0 ? state.fuel_type : undefined,
          transmission: state.transmission.length > 0 ? state.transmission : undefined,
          budget: (state.budget.min !== budgetDefaults.min || state.budget.max !== budgetDefaults.max) ? state.budget : undefined,
          is_express_delivery: state.is_express_delivery,
          feature: state.feature.length > 0 ? state.feature : undefined,
          sort_by: state.sort_by.length > 0 ? state.sort_by : undefined,
          seat_capacity: state.seat_capacity.length > 0 ? state.seat_capacity : undefined,
        }
      };

    case 'CLEAR_ALL_FILTERS':
      const clearBudgetDefaults = action.budgetDefaults || { min: 300000, max: 30000000 };
      return {
        ...initialState,
        budget: { min: clearBudgetDefaults.min, max: clearBudgetDefaults.max },
        appliedFilters: {}
      };

    case 'LOAD_FILTER_OPTIONS':
      const apiData = action.payload as ApiFilterResponse;
      const newBudget = apiData?.budget ? {
        min: apiData.budget.default_min_price,
        max: apiData.budget.default_max_price
      } : state.budget;
      
      return {
        ...state,
        budget: newBudget
      };

    case 'LOAD_FROM_URL':
      const urlFilters = action.payload as Partial<FilterState>;
      return {
        ...state,
        ...urlFilters,
        appliedFilters: {
          body_type: urlFilters.body_type,
          brand_model: urlFilters.brand_model,
          fuel_type: urlFilters.fuel_type,
          transmission: urlFilters.transmission,
          budget: urlFilters.budget,
          is_express_delivery: urlFilters.is_express_delivery,
          feature: urlFilters.feature,
          sort_by: urlFilters.sort_by,
          seat_capacity: urlFilters.seat_capacity,
        }
      };

    case 'SET_FILTER_LOADING':
      return state;

    case 'SET_FILTER_ERROR':
      return state;

    default:
      return state;
  }
};

const FilterContext = createContext<FilterContextType | undefined>(undefined);

interface FilterProviderProps {
  children: React.ReactNode;
  collectionSlug?: string;
  initialFilterOptions?: ApiFilterResponse | null;
}

export const FilterProvider: React.FC<FilterProviderProps> = ({ children, collectionSlug, initialFilterOptions }) => {
  const [state, dispatch] = useReducer(filterReducer, initialState);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [activeFilterType, setActiveFilterType] = useState<string | null>(null);
  const [filterOptions, setFilterOptions] = useState<ApiFilterResponse | null>(initialFilterOptions || null);
  const [isLoading, setIsLoading] = useState(!initialFilterOptions); // Only loading if no initial data
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(!!initialFilterOptions); // Already initialized if we have initial data
  
  const isMobileView = useIsMobile();
  const searchParams = useSearchParams();

  // Function to manually refresh filters (useful for force refresh scenarios)
  const refreshFilters = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const data = await filterService.getFilters(collectionSlug);
      
      if (data) {
        setFilterOptions(data);
        dispatch({ type: 'LOAD_FILTER_OPTIONS', payload: data });
        
        // Handle URL filters if they exist
        if (searchParams && hasUrlFilters(searchParams)) {
          const urlFilters = parseUrlFilters(searchParams);
          if (urlFilters.budget && !urlFilters.budget.min && !urlFilters.budget.max) {
            urlFilters.budget = {
              min: data.budget?.default_min_price || 300000,
              max: data.budget?.default_max_price || 30000000
            };
          }
          dispatch({ type: 'LOAD_FROM_URL', payload: urlFilters });
        }
        
        console.log('FilterContext: Filters refreshed successfully');
      } else {
        console.error('Failed to fetch filter data during refresh');
        setError('Failed to refresh filter options');
      }
      
    } catch (err) {
      console.error('Failed to refresh filter options:', err);
      setError('Failed to refresh filter options');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // If we already have initial data, just process it and skip the API call
    if (initialFilterOptions) {
      try {
        // Process the initial filter options
        dispatch({ type: 'LOAD_FILTER_OPTIONS', payload: initialFilterOptions });
        
        // Handle URL filters if they exist
        if (searchParams && hasUrlFilters(searchParams)) {
          const urlFilters = parseUrlFilters(searchParams);
          if (urlFilters.budget && !urlFilters.budget.min && !urlFilters.budget.max) {
            urlFilters.budget = {
              min: initialFilterOptions.budget?.default_min_price || 300000,
              max: initialFilterOptions.budget?.default_max_price || 30000000
            };
          }
          dispatch({ type: 'LOAD_FROM_URL', payload: urlFilters });
        }
        
        // Mark as initialized since we have data
        setIsInitialized(true);
        setIsLoading(false);
        
        console.log('FilterContext: Using initial filter options, skipping API call');
        
      } catch (err) {
        console.error('Failed to process initial filter options:', err);
        setError('Failed to process initial filter options');
        setIsLoading(false);
        setIsInitialized(true);
      }
      return; // Exit early, no need to make API call
    }

    // Only make API call if we don't have initial data
    const initializeFilters = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const data = await filterService.getFilters(collectionSlug);
        
        if (data) {
          setFilterOptions(data);
          dispatch({ type: 'LOAD_FILTER_OPTIONS', payload: data });
          
          if (searchParams && hasUrlFilters(searchParams)) {
            const urlFilters = parseUrlFilters(searchParams);
            if (urlFilters.budget && !urlFilters.budget.min && !urlFilters.budget.max) {
              urlFilters.budget = {
                min: data.budget?.default_min_price || 300000,
                max: data.budget?.default_max_price || 30000000
              };
            }
            dispatch({ type: 'LOAD_FROM_URL', payload: urlFilters });
          }
        } else {
          console.error('Failed to fetch filter data');
        }
        
      } catch (err) {
        console.error('Failed to fetch filter options:', err);
        setError('Failed to load filter options');
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    };

    initializeFilters();
  }, [collectionSlug, searchParams, initialFilterOptions]);

  const dispatchWithDefaults = (action: FilterAction) => {
    const budgetDefaults = {
      min: filterOptions?.budget?.default_min_price ?? 0,
      max: filterOptions?.budget?.default_max_price ?? 0
    };
    
    dispatch({
      ...action,
      budgetDefaults
    });

    if (isInitialized && (action.type === 'APPLY_FILTERS' || action.type === 'CLEAR_ALL_FILTERS')) {
      setTimeout(() => {
        if (action.type === 'APPLY_FILTERS') {
          const currentState = filterReducer(state, { ...action, budgetDefaults });
          updateUrlWithFilters(currentState.appliedFilters, filterOptions, budgetDefaults);
        } else if (action.type === 'CLEAR_ALL_FILTERS') {
          updateUrlWithFilters({}, filterOptions, budgetDefaults);
        }
      }, 0);
    }
  };

  const value: FilterContextType = {
    state,
    dispatch: dispatchWithDefaults,
    isFilterModalOpen,
    setIsFilterModalOpen,
    activeFilterType,
    setActiveFilterType,
    isMobileView,
    filterOptions,
    isLoading,
    error,
    isInitialized,
    refreshFilters // Add refresh method to context
  };

  return (
    <FilterContext.Provider value={value}>
      {children}
    </FilterContext.Provider>
  );
};

export const useFilter = (): FilterContextType | any => {
  const context = useContext(FilterContext);
  if (!context) {
    console.error('useFilter must be used within a FilterProvider');
  }
  return context;
}; 
